import { ReactNode } from 'react';
import { cn } from '@/lib/utils.ts';
import { STATUS_TONE_COLORS, StatusTone } from '@/components/ui/statusTone.ts';

export interface MetricListRow {
  id: string;
  label: ReactNode;
  /** Doprecyzowanie pod etykietą — z czego liczona jest wartość. */
  note?: ReactNode;
  value: ReactNode;
  /** Dopisek pod wartością: porównanie z poprzednim sezonem, powód odchylenia. */
  delta?: ReactNode;
  /** Ton dopisku. Bez niego dopisek jest wyciszony — nie każda różnica coś znaczy. */
  tone?: StatusTone;
}

interface MetricListProps extends React.HTMLAttributes<HTMLDivElement> {
  rows: MetricListRow[];
}

/**
 * Lista wskaźników: etykieta z notą po lewej, wartość z dopiskiem po prawej.
 * Średnia cena, największa rezerwacja, przychód z opcji — wszystko, co czyta
 * się jako „nazwa i liczba”, a nie jako wiersz danych rekordu (od tego jest
 * `InfoCardRow`).
 */
const MetricList = ({ rows, className, ...props }: MetricListProps) => (
  <div className={cn('flex flex-col', className)} {...props}>
    {rows.map((row, index) => (
      <div
        key={row.id}
        className={cn(
          'flex items-center gap-3.5 py-3.5',
          index > 0 && 'border-t border-neutral-100',
        )}
      >
        <span className="min-w-0">
          <span className="block text-[15px] text-base-text">{row.label}</span>
          {row.note && (
            <span className="mt-0.5 block text-[13px] text-neutral-500">
              {row.note}
            </span>
          )}
        </span>
        <span className="ml-auto shrink-0 text-right">
          <span className="block whitespace-nowrap text-[17px] font-extrabold tabular-nums text-ink">
            {row.value}
          </span>
          {row.delta && (
            <span
              className={cn(
                'mt-0.5 block text-[13px]',
                !row.tone && 'text-neutral-500',
              )}
              style={
                row.tone
                  ? { color: STATUS_TONE_COLORS[row.tone].text }
                  : undefined
              }
            >
              {row.delta}
            </span>
          )}
        </span>
      </div>
    ))}
  </div>
);

export default MetricList;
